/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports -- Ignored via go/DSP-18766; jsx required at runtime for @jsxRuntime classic
import { jsx } from '@emotion/react';
import classnames from 'classnames';

import type { RichMediaLayout as MediaSingleLayout } from '@atlaskit/adf-schema';
import {
	akEditorMediaResizeHandlerPaddingWide,
	DEFAULT_EMBED_CARD_WIDTH,
} from '@atlaskit/editor-shared-styles';
import { fg } from '@atlaskit/platform-feature-flags';
import { VcMediaWrapperProps } from '@atlaskit/react-ufo/vc-media';

import { MEDIA_SINGLE_GUTTER_SIZE } from '../../media-single/constants';
import { getMediaSinglePixelWidth } from '../../media-single/getMediaSinglePixelWidth';
import type { EditorAppearance } from '../../types';
import { shouldAddDefaultWrappedWidth } from '../../utils/shouldAddDefaultWrappedWidth';

import { MediaSingleDimensionHelper, MediaWrapper } from './styled';
import type { MediaSingleSize } from './types';
export interface Props {
	children: React.ReactNode;
	className?: string;
	containerWidth?: number;
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dataAttributes?: Record<string, any>;
	editorAppearance?: EditorAppearance;
	fullWidthMode?: boolean;
	handleMediaSingleRef?: React.RefObject<HTMLDivElement>;
	hasFallbackContainer?: boolean;
	height: number;
	isInRenderer?: boolean;
	isInsideOfInlineExtension?: boolean;
	isLoading?: boolean;
	layout: MediaSingleLayout;
	lineLength: number;
	nodeType?: string;
	/**
	 * @private
	 * @deprecated Use {@link Props.size.width} instead.
	 * Cleanup ticket: https://product-fabric.atlassian.net/browse/ED-19076
	 */
	pctWidth?: number;
	size?: MediaSingleSize;
	width?: number;
}

/**
 * MediaSingle
 * @param root0
 * @param root0.layout
 * @param root0.width
 * @param root0.height
 * @param root0.containerWidth
 * @param root0.isLoading
 * @param root0.pctWidth
 * @param root0.size
 * @param root0.className
 * @param root0.children
 * @param root0.nodeType
 * @param root0.fullWidthMode
 * @param root0.lineLength
 * @param root0.hasFallbackContainer
 * @param root0.handleMediaSingleRef
 * @param root0.isInsideOfInlineExtension
 * @param root0.dataAttributes
 * @param root0.isInRenderer
 * @example
 */
export default function MediaSingle({
	layout,
	width,
	height,
	containerWidth = width,
	isLoading = false,
	pctWidth,
	size,
	className,
	children: propsChildren,
	nodeType = 'mediaSingle',
	fullWidthMode,
	lineLength: editorWidth,
	hasFallbackContainer = true,
	handleMediaSingleRef,
	isInsideOfInlineExtension = false,
	dataAttributes,
	isInRenderer = false,
}: Props): jsx.JSX.Element {
	const isPixelWidth = size?.widthType === 'pixel';

	let mediaSingleWidth = size?.width || pctWidth;

	const children = React.Children.toArray(propsChildren);
	if (!mediaSingleWidth && shouldAddDefaultWrappedWidth(layout, width, editorWidth)) {
		// if width is not available, set to half of editor width
		mediaSingleWidth = isPixelWidth ? editorWidth / 2 : 50;
	}
	// When width is not set we have an absolute height for a given embed.
	// When both width and height are set we use them to determine ratio and use that to define
	// embed height in relation to whatever width of an dom element is in runtime.
	const isHeightOnly = width === undefined;
	// Preserve the absolute height before it is scaled below, so we can fall back to it if the
	// pixel-width computation yields a non-finite value (e.g. editorWidth/lineLength is 0 or
	// undefined in some host renderers such as Jira issue view).
	const absoluteHeight = height;
	if (mediaSingleWidth) {
		const pxWidth = getMediaSinglePixelWidth(
			mediaSingleWidth,
			editorWidth,
			size?.widthType,
			MEDIA_SINGLE_GUTTER_SIZE,
		);
		if (isHeightOnly) {
			width = pxWidth - akEditorMediaResizeHandlerPaddingWide;
		} else if (width !== undefined) {
			height = (height / width) * pxWidth;
			width = pxWidth;
		}
	} else if (isHeightOnly) {
		// No mediaSingleWidth can be found on already existing pages with existing embeds

		// It's ok to use Embed specific width, because width can be not set only in embed card.
		// This value will be used only in the case of non `wide` and non `full-width` cases inside MediaSingleDimensionHelper.
		width = DEFAULT_EMBED_CARD_WIDTH - akEditorMediaResizeHandlerPaddingWide;
	}

	// Media wrapper controls the height of the box.
	// We can define this height
	// - via height directly
	// - via paddingBottom (if we have both height and width) which is a css trick to represent a ratio
	let mediaWrapperHeight: number | undefined;
	let paddingBottom: string | undefined;
	if (isHeightOnly) {
		mediaWrapperHeight = height;
	} else if (
		width !== undefined &&
		!Number.isFinite((height / width) * 100) &&
		fg('platform_editor_embed_height_only_fallback')
	) {
		// The aspect-ratio (padding-bottom) trick collapses to 0 when the ratio is non-finite,
		// which happens when the resolved pixel width is 0/NaN (e.g. editorWidth/lineLength is
		// unavailable in the host renderer). Fall back to the embed's absolute height so the box
		// keeps a usable height instead of collapsing.
		// For embedCard nodes the `height` prop is the iframe height only; the normal ratio path
		// adds 32px for the embed header (see `calc(... + 32px)` below), so add it here too to
		// avoid clipping the header.
		mediaWrapperHeight = Number.isFinite(absoluteHeight)
			? absoluteHeight + (nodeType === 'embedCard' ? 32 : 0)
			: undefined;
	} else if (width !== undefined) {
		const mediaWrapperRatio = (height / width) * 100;
		paddingBottom = `${mediaWrapperRatio.toFixed(3)}%`;
		if (nodeType === 'embedCard') {
			// we want to set ratio of whole box (including header) buy knowing ratio of iframe itself

			// For some reason importing `embedHeaderHeight` from '@atlaskit/smart-card' breaks
			// packages/editor/editor-core/src/plugins/table/__tests__/unit/toolbar.ts 🤷‍️, but we have a test
			// that uses imported value, so it's should be good.
			paddingBottom = `calc(${paddingBottom} + 32px)`;
		}
	}

	const [media, caption] = children;

	return (
		<div
			ref={handleMediaSingleRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			css={MediaSingleDimensionHelper({
				width,
				layout,
				containerWidth,
				mediaSingleWidth,
				fullWidthMode,
				isExtendedResizeExperienceOn: isPixelWidth,
				isInsideOfInlineExtension,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				isInRenderer: isInRenderer && fg('media-perf-uplift-mutation-fix'),
			})}
			// eslint-disable-next-line react/jsx-props-no-spreading, @atlaskit/platform/no-preconditioning
			{...(!fg('platform_editor_fix_media_in_renderer') ? {} : { 'data-layout': layout })}
			data-width={mediaSingleWidth}
			data-width-type={size?.widthType || 'percentage'}
			data-node-type={nodeType}
			data-vc="media-single"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={classnames(
				'rich-media-item mediaSingleView-content-wrap',
				`image-${layout}`,
				className,
				{
					'is-loading': isLoading,
					'rich-media-wrapped': layout === 'wrap-left' || layout === 'wrap-right',
				},
			)}
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...dataAttributes}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...VcMediaWrapperProps}
		>
			<MediaWrapper
				hasFallbackContainer={hasFallbackContainer}
				height={mediaWrapperHeight}
				paddingBottom={paddingBottom}
			>
				{media}
			</MediaWrapper>
			{caption}
		</div>
	);
}
